import {
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaDownload } from 'react-icons/fa6';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { listCashbacks, updateCashbackPayment } from 'services/cashback';
import type {
  Cashback,
  CashbackSortField,
  CashbackSortOrder,
} from 'services/interfaces/cashback';
import PageSizePaginationFooter from 'shared/components/PageSizePaginationFooter';
import { usePermissions } from 'shared/hooks/usePermissions';
import CashbackFilters, {
  CashbackFiltersValues,
} from './components/CashbackFilters';
import CashbackPaymentModal from './components/CashbackPaymentModal';
import CashbackTable from './components/CashbackTable';
import { getInitialCashbackFilters } from './helpers/initialCashbackFilters';
import { exportCashbacksXlsx } from './helpers/formatCashback';

function isAbortError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { name?: string; code?: string };
  return (
    err.name === 'AbortError' ||
    err.name === 'CanceledError' ||
    err.code === 'ERR_CANCELED'
  );
}

const CashbackControlPage = () => {
  const { e } = usePermissions();
  const canRead = e('cashback-read');
  const canUpdate = e('cashback-update');
  const bgColor = useColorModeValue('white', 'gray.800');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<CashbackSortField>('requested_at');
  const [sortOrder, setSortOrder] = useState<CashbackSortOrder>('desc');
  const [filters, setFilters] = useState<CashbackFiltersValues>(() =>
    getInitialCashbackFilters(),
  );
  const [searchDebounced, setSearchDebounced] = useState('');

  const [rows, setRows] = useState<Cashback[]>([]);
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalRow, setModalRow] = useState<Cashback | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = rows.length > 0 && rows.every((r) => prev.has(r.id));
      if (allSelected) {
        return new Set();
      }
      return new Set(rows.map((r) => r.id));
    });
  }, [rows]);

  const handleExport = () => {
    const toExport = selectedRows.length > 0 ? selectedRows : rows;
    if (toExport.length === 0) return;
    exportCashbacksXlsx(toExport, 'cashbacks.xlsx');
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(filters.search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  const onFiltersChange = (patch: Partial<CashbackFiltersValues>) => {
    setFilters((f) => ({ ...f, ...patch }));
    const keys = Object.keys(patch) as (keyof CashbackFiltersValues)[];
    if (
      keys.some((k) => k === 'status' || k === 'startDate' || k === 'endDate')
    ) {
      setPage(1);
    }
  };

  const loadList = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const res = await listCashbacks(
          {
            page,
            perPage,
            sortBy,
            sortOrder,
            status: filters.status || undefined,
            search: searchDebounced || undefined,
            start_date: filters.startDate || undefined,
            end_date: filters.endDate || undefined,
          },
          signal,
        );
        if (signal.aborted) return;
        setRows(res.data);
        setMeta(res.meta);
      } catch (e) {
        if (isAbortError(e) || signal.aborted) return;
        setError('Não foi possível carregar os resgates de cashback.');
        setRows([]);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [
      page,
      perPage,
      sortBy,
      sortOrder,
      filters.status,
      filters.startDate,
      filters.endDate,
      searchDebounced,
    ],
  );

  useEffect(() => {
    const ac = new AbortController();
    loadList(ac.signal);
    return () => ac.abort();
  }, [loadList]);

  const handleSort = (field: CashbackSortField) => {
    setPage(1);
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'requested_at' ? 'desc' : 'asc');
    }
  };

  const handlePerPageChange = (n: number) => {
    setPerPage(n);
    setPage(1);
  };

  const openPaymentModal = (row: Cashback) => {
    setModalRow(row);
    setModalOpen(true);
  };

  const closePaymentModal = () => {
    if (submittingPayment) return;
    setModalOpen(false);
    setModalRow(null);
  };

  const handleConfirmPayment = async (payload: {
    paid: boolean;
    payment_date: string;
  }) => {
    if (!modalRow) return;
    if (payload.paid && !payload.payment_date.trim()) {
      toast.error('Informe a data de pagamento.');
      return;
    }
    const ac = new AbortController();
    setSubmittingPayment(true);
    setActionLoadingId(modalRow.id);
    try {
      await updateCashbackPayment(modalRow.id, payload, ac.signal);
      toast.success('Pagamento atualizado com sucesso.');
      setModalOpen(false);
      setModalRow(null);
      await loadList(ac.signal);
    } catch (e) {
      if (!isAbortError(e)) {
        toast.error('Falha ao atualizar o pagamento.');
      }
    } finally {
      setSubmittingPayment(false);
      setActionLoadingId(null);
    }
  };

  const showInitialSpinner = loading && rows.length === 0 && !error;
  const showTable =
    !error && (rows.length > 0 || (!loading && rows.length === 0));

  if (!canRead) {
    return (
      <Stack
        h="100vh"
        align="center"
        justify="center"
        gap="1.5rem"
        textAlign="center"
        p="2rem"
      >
        <Heading size="lg" color="gray.600">
          Acesso Restrito
        </Heading>
        <Text color="gray.500" maxW="400px">
          Você não possui permissão para acessar o módulo de cashbacks. Solicite
          ao seu supervisor para liberar o acesso.
        </Text>
        <Box>
          <Button as={RouterLink} to="/" colorScheme="yooga">
            Voltar para a página inicial
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      minH="100vh"
      minW={{ lg: '740px' }}
      w={{ base: '90%', lg: '85%' }}
      m="auto"
      px={{ base: '1rem', lg: '4rem' }}
      pt={{ base: '1rem', lg: '1.5rem' }}
      pb={{ base: '2rem', lg: '4rem' }}
      gap="1.5rem"
    >
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Heading size="lg" color="gray.600">
          Cashbacks - Clube Yooga
        </Heading>

        <Button
          size="sm"
          colorScheme="yooga"
          variant="outline"
          leftIcon={<FaDownload />}
          isDisabled={rows.length === 0}
          onClick={handleExport}
        >
          Exportar Excel
          {selectedRows.length > 0 && ` (${selectedRows.length})`}
        </Button>
      </HStack>

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Stack spacing={4} mb={6}>
          <CashbackFilters
            values={filters}
            onChange={onFiltersChange}
            disabled={loading && rows.length === 0}
          />
        </Stack>

        {error && !showInitialSpinner && (
          <Stack align="center" py="4rem" gap="1rem">
            <Text color="red.500">{error}</Text>
            <Button
              colorScheme="yooga"
              variant="outline"
              onClick={() => {
                const ac = new AbortController();
                loadList(ac.signal);
              }}
            >
              Tentar novamente
            </Button>
          </Stack>
        )}

        {showInitialSpinner && (
          <HStack justify="center" py="4rem">
            <Spinner size="lg" color="yooga.500" />
            <Text>Carregando…</Text>
          </HStack>
        )}

        {showTable && (
          <>
            {!loading && rows.length === 0 && (
              <Text color="gray.500" py={8} textAlign="center">
                Nenhum registro encontrado com os filtros atuais.
              </Text>
            )}
            {rows.length > 0 && (
              <CashbackTable
                rows={rows}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                onOpenPayment={openPaymentModal}
                actionLoadingId={actionLoadingId}
                listLoading={loading}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                canUpdate={canUpdate}
              />
            )}
            <Box pt={4} w="100%">
              <PageSizePaginationFooter
                totalLength={meta.total}
                visibleLength={rows.length}
                page={meta.page}
                setPage={setPage}
                pageSize={perPage}
                setPageSize={handlePerPageChange}
                disabled={loading}
                pageSizes={[10, 20, 50]}
              />
            </Box>
          </>
        )}
      </Box>

      <CashbackPaymentModal
        isOpen={modalOpen}
        onClose={closePaymentModal}
        row={modalRow}
        onConfirm={handleConfirmPayment}
        submitting={submittingPayment}
      />
    </Stack>
  );
};

export default CashbackControlPage;
